"use client";

import { setChatbotOpen } from "@/store/feature/common/commonSlice";
import { useAppDispatch } from "@/store/hooks";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

const AiMentor = () => {
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const list = [
    {
      image: "/assets/images/png/homepage/marking.png",
      title: "Profile Matching",
      description: "6-factor Fit Score across available programs worldwide",
    },
    {
      image: "/assets/images/png/homepage/mony.png",
      title: "Scholarship Finder",
      description: "Surfaces grants you qualify for - ₹1.5L to ₹10L found",
    },
    {
      image: "/assets/images/png/homepage/sop.png",
      title: "SOP & Document Help",
      description: "Step-by-step guidance on SOP, LOR, transcripts & visa",
    },
    {
      image: "/assets/images/png/homepage/visa.png",
      title: "Visa Guidance",
      description: "Step-by-step visa guidance for the UK. More countries coming soon.",
    },
  ];

  const handleOpenChatBot = () => {
    dispatch(setChatbotOpen(true));
  };

  // Do not render anything until we know the viewport so we don't mount both videos.
  const videoSrc =
    isMobile === null
      ? null
      : isMobile
        ? "/assets/mp4/homeChatMobile.mp4"
        : "/assets/mp4/homeChatDesktop.mp4";

  return (
    <div className="bg-[linear-gradient(160deg,_#120538_8.49%,_#1D0A45_54.15%,_#2D0A6B_91.51%)] ">
      <div className="container max-w-[1400px] mx-auto py-12 sm:py-16 md:py-20 lg:py-24 px-4 xxs:px-6 sm:px-[40px]">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-16">

          {/* Left Column (Content) */}
          <div className="w-full lg:max-w-[550px] flex flex-col items-center sm:items-start text-center sm:text-left">
            <h2 className="text-white font-semibold mx-auto sm:mx-0 text-[20px] sm:text-[36px] md:text-[40px] lg:text-[44px] xl:text-[48px] leading-[1.2]">
              Your AI Mentor,{" "}
              <br className="hidden sm:inline" />
              <span className="text-[#1776F0] ff_poppins">Available 24/7</span>
            </h2>

            {/* Feature List (Desktop Only) */}
            <div className="gap-8 flex-col mt-8 hidden lg:flex">
              {list.map((item, index) => (
                <div key={index} className="flex items-start gap-5 text-white">
                  <div className="border-[1.42px] border-[#BED1FF] rounded-[11.39px] bg-[#F6F9FF] p-3 shrink-0">
                    <Image
                      className="w-10 h-10 object-contain"
                      src={item.image}
                      alt={item.title}
                      width={40}
                      height={40}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg ff_poppins">
                      {item.title}
                    </h3>
                    <p className="font-normal text-white/70 text-sm ff_poppins mt-1 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column (Chatbot Preview) */}
          <div className="w-full max-w-[340px] xxs:max-w-[380px] sm:max-w-[480px] md:max-w-[600px] xl:max-w-[697px]">
            <div
              ref={containerRef}
              onClick={handleOpenChatBot}
              className="relative overflow-hidden rounded-[24px] sm:rounded-[32px] shadow-[0px_8px_32px_rgba(0,0,0,0.3)] lg:shadow-[0px_0px_50px_rgba(173,131,247,0.25)] cursor-pointer w-full bg-[#060709] min-h-[260px] sm:min-h-[380px]"
            >
              {isVisible && videoSrc ? (
                <video
                  key={videoSrc}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto block"
                  preload="metadata"
                >
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full h-full min-h-[260px] sm:min-h-[380px] flex items-center justify-center bg-[#060709]/50">
                  <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AiMentor;
